/* Les trois prix, et ce qui se passe entre eux.
 *
 *   prix usine     ce que l'usine demande. La direction seule le voit : il ne
 *                  descend même pas dans le navigateur d'un agent, la base
 *                  refuse les lignes.
 *   plancher       le prix de vente minimum d'un agent sur une référence. Posé
 *                  par la direction, à défaut le prix de vente de référence
 *                  porté par le produit.
 *   prix vendu     ce que l'agent a réellement inscrit sur le devis.
 *
 * Au-dessus de son plancher, l'agent vend ce qu'il veut. La différence ne se
 * partage que si une règle existe pour l'usine — toutes ne le permettent pas,
 * et sans règle il n'y a rien à partager.
 */

import type {
  LigneVente, PrixAgent, PrixClient, PrixUsine, Produit, RegleSurplus,
} from "@/types/metier";

/** Ce qui est en vigueur : une période ouverte, c'est-à-dire sans fin. */
export function enVigueur<T extends { fin_le: string | null }>(lignes: readonly T[]): T[] {
  return lignes.filter((l) => l.fin_le === null);
}

/** L'archive d'une référence, du plus récent au plus ancien. */
export function historique<T extends { debut_le: string }>(lignes: readonly T[]): T[] {
  return [...lignes].sort((a, b) => b.debut_le.localeCompare(a.debut_le));
}

/** Planchers d'un agent, par produit : sa ligne à lui, sinon le prix du produit. */
export function planchersDe(
  produits: readonly Produit[],
  prixAgent: readonly PrixAgent[],
  agentId: string,
): Map<string, number> {
  const m = new Map<string, number>();
  for (const p of produits) if (p.prix != null && p.prix > 0) m.set(p.id, p.prix);
  for (const pa of enVigueur(prixAgent)) {
    if (pa.agent_id === agentId) m.set(pa.produit_id, pa.prix_mini);
  }
  return m;
}

/**
 * Ce que ce client paie, référence par référence.
 *
 * Son tarif s'il en a un, sinon le plancher de l'agent qui le sert. Le tarif
 * client peut être au-dessus du plancher — c'est même ce qu'on cherche — mais
 * jamais en dessous : la direction seule peut l'y mettre, et dans ce cas c'est
 * une décision, pas un oubli.
 */
export function tarifsDe(
  planchers: ReadonlyMap<string, number>,
  prixClient: readonly PrixClient[],
  clientId: string | null,
): Map<string, number> {
  const m = new Map(planchers);
  if (!clientId) return m;
  for (const pc of enVigueur(prixClient)) {
    if (pc.client_id === clientId) m.set(pc.produit_id, pc.prix);
  }
  return m;
}

/** Une hausse à répercuter sur le tarif du client. */
export interface Hausse {
  produitId: string;
  ref: string;
  avant: number | null;
  apres: number;
}

/**
 * Ce qu'un devis apprend au tarif du client.
 *
 * Seules les hausses remontent. Une remise ponctuelle ne doit pas effacer un
 * tarif durement obtenu : si la ligne part moins cher que le tarif en cours,
 * le tarif ne bouge pas.
 */
export function haussesDe(
  lignes: readonly Pick<LigneVente, "produit_id" | "ref" | "prix">[],
  tarifs: ReadonlyMap<string, number>,
): Hausse[] {
  const sorties: Hausse[] = [];
  const vus = new Set<string>();
  for (const l of lignes) {
    if (!l.produit_id || vus.has(l.produit_id) || !(l.prix > 0)) continue;
    const avant = tarifs.get(l.produit_id) ?? null;
    if (avant !== null && l.prix <= avant) continue;
    vus.add(l.produit_id);
    sorties.push({ produitId: l.produit_id, ref: l.ref, avant, apres: l.prix });
  }
  return sorties;
}

/** Marge brute d'Omnix sur une référence : plancher moins prix usine. */
export function margeDe(plancher: number | null, prixUsine: number | null): number | null {
  if (plancher == null || prixUsine == null) return null;
  return Math.round((plancher - prixUsine) * 10000) / 10000;
}

/** Prix usine par produit, indexé. Vide pour qui n'a pas le droit de le voir. */
export function indexPrixUsine(lignes: readonly PrixUsine[]): Map<string, number> {
  return new Map(enVigueur(lignes).map((l) => [l.produit_id, Number(l.prix)] as const));
}

/** Règle d'excédent applicable : la règle de gamme bat la règle d'usine. */
export function regleSurplusDe(
  regles: readonly RegleSurplus[],
  usineId: string,
  gamme: string | null,
): RegleSurplus | null {
  let large: RegleSurplus | null = null;
  let precise: RegleSurplus | null = null;
  for (const r of regles) {
    if (!r.actif || r.usine_id !== usineId) continue;
    if (r.gamme) {
      if (gamme && r.gamme === gamme) precise = r;
    } else large = r;
  }
  return precise ?? large;
}

/** Une ligne vendue sous son plancher — ce que le devis doit signaler. */
export interface SousPlancher {
  ref: string;
  prix: number;
  plancher: number;
}

export function sousPlancher(
  lignes: readonly Pick<LigneVente, "produit_id" | "ref" | "prix">[],
  planchers: ReadonlyMap<string, number>,
): SousPlancher[] {
  const sorties: SousPlancher[] = [];
  for (const l of lignes) {
    if (!l.produit_id) continue;
    const p = planchers.get(l.produit_id);
    if (p == null || !(l.prix > 0)) continue;
    if (l.prix < p) sorties.push({ ref: l.ref, prix: l.prix, plancher: p });
  }
  return sorties;
}

export interface Excedent {
  /** Vendu au-dessus du plancher, toutes lignes confondues. */
  montant: number;
  /** Ce que la règle en rend à l'agent. Zéro quand aucune règle ne s'applique. */
  part: number;
}

export const SANS_EXCEDENT: Excedent = { montant: 0, part: 0 };

/**
 * Excédent d'une vente et part qui revient à l'agent.
 *
 * Ligne à ligne, comme le barème : une vente peut mélanger des références
 * dont l'usine partage l'excédent et d'autres non.
 */
export function excedentDe(
  lignes: readonly Pick<LigneVente, "produit_id" | "gamme" | "prix" | "qte">[],
  usineId: string,
  planchers: ReadonlyMap<string, number>,
  regles: readonly RegleSurplus[],
): Excedent {
  let montant = 0;
  let part = 0;
  for (const l of lignes) {
    if (!l.produit_id) continue;
    const plancher = planchers.get(l.produit_id);
    if (plancher == null) continue;
    const ecart = (l.prix || 0) - plancher;
    if (ecart <= 0) continue;
    const surplus = ecart * (l.qte || 0);
    montant += surplus;
    const r = regleSurplusDe(regles, usineId, l.gamme);
    if (r) part += (surplus * r.pct_agent) / 100;
  }
  return { montant: arrondi2(montant), part: arrondi2(part) };
}

function arrondi2(v: number): number {
  return Math.round(v * 100) / 100;
}
